// features/zones/ZoneCreationPage.jsx
import React, { useEffect } from "react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { Card, Button, Modal, Form, Space } from "antd";
import { ExpandOutlined, PlusOutlined } from "@ant-design/icons";
import { DrawingTools } from "./components/DrawingTools";
import { ExistingPolygons } from "./components/ExistingPolygons";
import { ZoneTable } from "./components/ZoneTable";
import { ZoneForm } from "./components/ZoneForm";
import { useZoneManagement } from "./hooks/useZoneManagement";
import { NavLink } from "react-router-dom";
import PermissionRender from "../../components/PermissionRender";

const ZoneCreationPage = () => {
  const [form] = Form.useForm();
  const {
    zones,
    currentZone,
    isModalVisible,
    loading,
    drawingMode,
    editingZone,
    isEditing,
    zoomToZone,
    fetchZones,
    handlePolygonComplete,
    startEditingZone,
    handlePolygonEdit,
    handleViewZone,
    cancelEditing,
    handleSave,
    handleModalClose,
    deleteZone,
    setDrawingMode,
    setIsModalVisible,
  } = useZoneManagement(form);

  useEffect(() => {
    fetchZones();
  }, []);

  const handleDeleteZone = (zoneId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette zone ?",
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      width: 480,
      cancelText: "Non",
      onOk: () => deleteZone(zoneId),
    });
  };
  const openEditModal = () => {
    if (editingZone) {
      form.setFieldsValue({
        name: editingZone.name,
        prefix: editingZone.prefix,
        description: editingZone.description,
      });
      setIsModalVisible(true);
    }
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} region="MA">
      <div style={{ padding: 24 }}>
        <Card
          title={
            <>
              <span>Gestion des contrats</span>
              <NavLink
                to="/preview-map"
                className="ml-3 text-xl p-1 shadow-lg bg-purple-100 rounded-lg"
              >
                <ExpandOutlined />
              </NavLink>
            </>
          }
          extra={
            <PermissionRender
              requiredPermission={["zone", "create"]}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setDrawingMode(true)}
                disabled={isEditing}
              >
                Dessiner une nouvelle contrat
              </Button>
            </PermissionRender>
          }
        >
          {isEditing && (
            <div
              style={{
                marginBottom: 16,
                backgroundColor: "#fff2f0",
                padding: 16,
                borderRadius: 4,
              }}
            >
              <Space>
                <span>Contrat en cours d'édition : {editingZone.name}</span>
                <Button type="primary" onClick={openEditModal}>
                  Enregistrer les modifications
                </Button>
                <Button danger onClick={cancelEditing}>
                  Annuler
                </Button>
              </Space>
            </div>
          )}

          <div style={{ height: "50vh", marginBottom: 24 }}>
            <Map
              disableDefaultUI={false}
              options={{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
              }}
              defaultCenter={{ lat: 35.688469, lng: -5.688669 }}
              defaultZoom={11}
              gestureHandling="greedy"
              styles={[
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ]}
            >
              {drawingMode && (
                <DrawingTools onPolygonComplete={handlePolygonComplete} />
              )}
              <ExistingPolygons
                zones={zones}
                editingZone={editingZone}
                onPolygonEdit={handlePolygonEdit}
                zoomToZone={zoomToZone}
                isEditing={isEditing}
              />
            </Map>
          </div>

          <ZoneTable
            zones={zones}
            loading={loading}
            editingZone={editingZone}
            isEditing={isEditing}
            onView={handleViewZone}
            onEdit={startEditingZone}
            onDelete={handleDeleteZone}
            onSave={openEditModal}
            onCancel={cancelEditing}
          />
        </Card>

        <Modal
          title={
            editingZone
              ? "Enregistrer les modifications de la contrat"
              : "Créer une nouvelle contrat"
          }
          open={isModalVisible}
          onOk={form.submit}
          onCancel={handleModalClose}
          confirmLoading={loading}
        >
          <ZoneForm form={form} onFinish={handleSave} />
        </Modal>
      </div>
    </APIProvider>
  );
};

export default ZoneCreationPage;
